/**
 * The buyer's session, kept separate from staff and from farmers.
 *
 * Its own storage keys for the same reason the farmer's has its own: one phone can be
 * shared, and signing one person in must never sign another out or — far worse — hand
 * one of them somebody else's token. The three never touch.
 */
import { ApiError } from './api'
import type { PhotoRef } from './photos'

const TOKEN_KEY = 'ledger.buyer.token'
const WHO_KEY = 'ledger.buyer.who'

export interface BuyerIdentity {
  id: string
  name: string
  contactName: string
  /** Some people at a buyer may look but not commit the business to anything. */
  canOrder: boolean
}

export const buyerSession = {
  get token() {
    return localStorage.getItem(TOKEN_KEY)
  },
  get who(): BuyerIdentity | null {
    const raw = localStorage.getItem(WHO_KEY)
    return raw ? (JSON.parse(raw) as BuyerIdentity) : null
  },
  get isSignedIn() {
    return !!localStorage.getItem(TOKEN_KEY)
  },
  set(token: string, who: BuyerIdentity) {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(WHO_KEY, JSON.stringify(who))
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(WHO_KEY)
  },
}

async function parse(res: Response) {
  const text = await res.text()
  if (!text.trim()) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function request<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = buyerSession.token
  if (auth && token) headers.Authorization = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'OFFLINE', 'No connection. Try again when you have network.')
  }

  const payload = await parse(res)

  if (!res.ok) {
    if (res.status === 401 && auth && token) buyerSession.clear()
    const err = (payload as { error?: { code?: string; message?: string } })?.error
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? 'Something went wrong. Try again.',
    )
  }

  return payload as T
}

export const buyerApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),

  requestCode: (msisdn: string) =>
    request<{ sent: boolean; message: string; devCode?: string; devNote?: string }>(
      'POST', '/buyer-auth/request-code', { msisdn }, false),

  async verify(msisdn: string, code: string) {
    const res = await request<{ accessToken: string; buyer: BuyerIdentity }>(
      'POST', '/buyer-auth/verify', { msisdn, code }, false)
    buyerSession.set(res.accessToken, res.buyer)
    return res.buyer
  },

  signOut() {
    buyerSession.clear()
  },
}

// ---------------------------------------------------------------- shapes
export interface ShopItem {
  cropId: string
  cropName: string
  localName: string | null
  gradeId: string
  gradeCode: string
  gradeName: string
  specText: string
  pricePerKg: number
  /** True when this price was agreed with them, rather than today's list price. */
  isYourPrice: boolean
  availableKg: number
}

export interface BuyerProfile {
  id: string
  name: string
  buyerType: string
  contactName: string
  canOrder: boolean
  address: string | null
  windowStart: string | null
  windowEnd: string | null
  payment: {
    terms: string
    termsDays: number
    limit: number
    owing: number
    overdue: number
  }
  ordersOpen: number
}

export interface BuyerOrderLine {
  cropName: string
  gradeCode: string
  orderedKg: number
  unitPrice: number | null
  acceptedKg: number | null
}

export interface BuyerOrder {
  id: string
  code: string
  status: string
  orderedAt: string
  deliveryDate: string
  orderedKg: number
  total: number
  notes: string | null
  lines: BuyerOrderLine[]
}

export interface BuyerDelivery {
  id: string
  code: string
  status: string
  plannedAt: string | null
  completedAt: string | null
  acceptedKg: number
  rejectedKg: number
  signedBy: string | null
  signedRole: string | null
  signatureRef: PhotoRef | null
  photoRef: PhotoRef | null
  orderCode: string
  tripCode: string | null
  driverName: string | null
  lines: Array<{
    cropName: string; gradeCode: string
    acceptedKg: number; rejectedKg: number; unitPrice: number | null
  }>
}

export interface BuyerBill {
  id: string
  number: string
  issueDate: string
  dueDate: string
  total: number
  amountPaid: number
  balance: number
  status: string
  taxInvoiceNo: string | null
  daysOverdue: number
  deliveryCode: string | null
}

/** What an order's state means to the person who placed it. */
export function orderStatusLabel(status: string): { text: string; tone: 'ok' | 'warn' | 'crit' | 'info' | 'neutral' } {
  switch (status) {
    case 'draft': return { text: 'We will call you', tone: 'warn' }
    case 'confirmed': return { text: 'Got it', tone: 'info' }
    case 'allocated': return { text: 'Set aside for you', tone: 'ok' }
    case 'picking': return { text: 'Being packed', tone: 'ok' }
    case 'ready': return { text: 'Packed, waiting for the van', tone: 'ok' }
    case 'dispatched': return { text: 'On the way', tone: 'ok' }
    case 'delivered': return { text: 'Delivered', tone: 'neutral' }
    case 'closed': return { text: 'Done', tone: 'neutral' }
    case 'cancelled': return { text: 'Cancelled', tone: 'neutral' }
    default: return { text: status.replace(/_/g, ' '), tone: 'neutral' }
  }
}
